<template>
    <div class="customer-contract">
        <div>
            <DCard
                :title="__('customers.contracts.contract_details')"
                :padding="true"
                :footer-padding="true"
            >
                <template #default>
                    <div class="detail">
                        <div>
                            <span>{{ __('customers.contracts.contract_id') }}</span>
                            <span>#{{ contract.id }}</span>
                            <span>
                                <IncidentFlag
                                    v-if="userPermissions.includes('customer.incidents_view') && contract.has_incidents"
                                    id="contract-incident-flag"
                                    :active-incident="contract.has_open_incidents"
                                    :relation-type="'contracts'"
                                    :relation-id="contract.id"
                                    :customer-id="contract.customer_id"
                                />
                            </span>
                        </div>
                        <div v-if="contract.contract_number !== null && contract.contract_number !== ''">
                            <span>{{ __('customers.contracts.contract_number') }}</span>
                            <span>{{ contract.contract_number }}</span>
                        </div>
                        <div class="two-columns">
                            <span>{{ __('customers.contracts.product') }}</span>
                            <span v-if="contract.active_contract_phase">
                                <Link
                                    v-if="userPermissions.includes('product.view')"
                                    :href="viewProductUrl"
                                >
                                    {{ productName }}
                                </Link>
                            </span>
                            <span v-else>
                                <DTag
                                    color="danger"
                                    :label="__('customers.contracts.no_contract_phases')"
                                />
                            </span>
                        </div>
                        <div class="contract-status-grid">
                            <span>{{ __('customers.contracts.status') }}</span>
                            <span>
                                <DTag :label="contract.status" />
                                <DBadge
                                    v-if="contract.manual_termination_disallowed === true"
                                    color="warning"
                                    class="me-1"
                                    :title="__('customers.contracts.non_terminable_hint')"
                                >
                                    {{ __('customers.contracts.non_terminable') }}
                                </DBadge>
                                <Link
                                    v-if="userPermissions.includes('contract.fulfillment')"
                                    :href="viewFulfillmentRequestUrl"
                                >
                                    {{ __('customers.contracts.fulfillment_requests') }}
                                </Link>
                                <span v-if="!contract.one_time && userPermissions.includes('contract.edit') && contract.status === 'suspended'"> | <a
                                    href="javascript:void(0);"
                                    @click="clickUnsuspend()"
                                >{{ __('customers.contracts.unsuspend_contract.name') }}
                                </a>
                                </span>

                            </span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.start_date') }}</span>
                            <span>{{ setDateFormatEuropeBerlin(contract.start_date) }}</span>
                        </div>
                        <div v-if="contract.lead_time !== null && contract.lead_time !== 0">
                            <span>{{ __('customers.contracts.fulfillment_lead_time') }}</span>
                            <span>
                                {{ contract.lead_time }} {{ __('messages.units.days') }}
                            </span>
                        </div>
                        <div v-if="contract.one_time === false">
                            <span>{{ __('customers.contracts.contract_term') }}</span>
                            <span v-if="contract.active_contract_phase">
                                {{ activeContractPhaseInfo }}
                            </span>
                            <span v-if="!contract.active_contract_phase">
                                <DTag
                                    color="danger"
                                    :label="__('customers.contracts.no_contract_phases')"
                                />
                            </span>
                        </div>
                        <div v-if="contract.parent">
                            <span>{{ __('customers.contracts.parent_contract') }}</span>
                            <span>
                                <Link
                                    :href="viewParentContractUrl"
                                >
                                    {{ contract.parent_contract_product }} ({{ contract.parent.id }})
                                </Link>
                            </span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.order.order_number') }}</span>
                            <span v-if="contract.order_id">
                                <Link
                                    v-if="userPermissions.includes('order.view')"
                                    :href="viewOrderUrl"
                                >
                                    {{ orderNumber }}
                                </Link>
                                <span v-else>
                                    {{ orderNumber }}
                                </span>
                            </span>
                            <DTag
                                v-else
                                :label="__('customers.contracts.order.missing_order_title')"
                            />
                        </div>
                        <div v-if="contract.previous_contract_ids.length > 0">
                            <span>{{ __('customers.contracts.previous_contracts') }}</span>
                            <span style="word-break: keep-all;">
                                <Link
                                    v-for="previousContract in contract.previous_contract_ids"
                                    :key="previousContract"
                                    :href="getPreviousContractUrl(previousContract)"
                                >
                                    {{ previousContract }} &nbsp;
                                </Link>
                            </span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.terms_and_conditions') }}</span>
                            <span v-if="contract.terms_and_conditions_id">
                                <Link
                                    v-if="userPermissions.includes('contract.view')"
                                    :href="$route('settings.terms-and-conditions.index')"
                                    @click="downloadFile"
                                >
                                    {{ contract.terms_and_conditions_version }}
                                </Link>
                                <span v-else>
                                    {{ orderNumber }}
                                </span>
                            </span>
                            <DTag
                                v-else
                                :label="__('customers.contracts.unknown_version')"
                            />
                        </div>
                        <div v-if="contract.active_termination_date !== null">
                            <span>{{ __('customers.contracts.termination_to') }}</span>
                            <span>
                                {{
                                    setDateFormatEuropeBerlin(contract.active_termination_date)
                                }} {{ extraordinaryTerminationText }}
                            </span>
                        </div>
                        <div v-if="contract.active_termination_entry_date !== null">
                            <span>{{ __('customers.contracts.termination_at') }}</span>
                            <span>{{ setDateFormatEuropeBerlin(contract.active_termination_entry_date) }}</span>
                        </div>
                        <div v-if="contract.active_termination_reason !== null">
                            <span>{{ __('customers.contracts.termination_reason') }}</span>
                            <span>{{ contract.active_termination_reason }}</span>
                        </div>
                        <div v-if="contract.active_termination_comment !== null">
                            <span>{{ __('customers.contracts.termination_comment') }}</span>
                            <span>{{ contract.active_termination_comment }}</span>
                        </div>
                    </div>
                </template>

                <template
                    #footer
                >
                    <DButton
                        v-if="canActivate && contract.status === 'wait_for_activation'"
                        id="activate_contract"
                        :label="__('customers.contracts.activate')"
                        :disabled="isInLoadingState"
                        @click="showActivateModal = true"
                    />
                    <DDropdown
                        v-if="contract.status !== 'terminated' && contract.status !== 'cancelled' && contract.status !== 'draft' && contract.one_time !== true && contract.is_ff_child_contract === false"
                        id="terminations_dropdown"
                        :toggler-text="__('customers.contracts.terminate')"
                        :disabled="!hasTerminationActions"
                        class="d-inline-flex terminate-dropdown"
                    >
                        <DDropdownItem
                            v-if="canTerminate"
                            id="terminate"
                            icon="close-octagon"
                            :disabled="isInLoadingState"
                            @click="terminateContract()"
                        >
                            {{ __('customers.contracts.terminate') }}
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="canCancelTermination"
                            id="cancel_termination"
                            icon="close-octagon"
                            :disabled="isInLoadingState"
                            @click="cancelContractTermination()"
                        >
                            {{ __('customers.contracts.cancel_terminations') }}
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="canApplyMoneyBackOrWithdraw"
                            id="money_back_guarantee"
                            icon="cash"
                            :disabled="isInLoadingState"
                            @click="applyMoneyBackGuarantee()"
                        >
                            {{ __('customers.contracts.apply_money_back_guarantee.name') }}
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="canApplyMoneyBackOrWithdraw"
                            id="action_withdraw"
                            icon="backup-restore"
                            :disabled="isInLoadingState"
                            @click="withdrawContract()"
                        >
                            {{ __('customers.contracts.withdrawal_contract.name') }}
                        </DDropdownItem>
                        <DDropdownItem
                            v-if="canTerminateSilently"
                            id="terminate_silently"
                            icon="close-octagon-outline"
                            :disabled="isInLoadingState"
                            @click="terminateContractSilently()"
                        >
                            {{ __('customers.contracts.terminate_silently') }}
                        </DDropdownItem>
                    </DDropdown>
                    <DButton
                        v-if="userPermissions.includes('contract.edit') && contract.supports_send_auth_code === true && contract.fulfillment_account_id !== null && contract.active_termination_status !== 'done'"
                        id="send_auth_code"
                        :label="__('customers.contracts.send_auth_code.send_auth_code')"
                        :disabled="isInLoadingState"
                        @click="openAuthCodeConfirmationModal"
                    />
                    <DButton
                        v-if="userPermissions.includes('contract.edit') && contract.parent_contract_id === null && contract.status !== 'terminated' && contract.status !== 'cancelled' && contract.is_ff_child_contract === false"
                        id="move_contract"
                        :label="__('customers.contracts.move_contract.move_contract')"
                        :disabled="isInLoadingState"
                        @click="moveContract()"
                    />
                    <DButton
                        v-if="showAbuseLockButton"
                        id="abuse_lock_contract"
                        :label="isAbuseLocked
                            ? __('customers.contracts.abuse_lock.disable')
                            : __('customers.contracts.abuse_lock.enable')"
                        :disabled="isInLoadingState"
                        @click="clickAbuseLock()"
                    />
                    <DButton
                        v-if="contract.quickstart_possible && contract.is_ff_child_contract === false"
                        id="quickstart_contract"
                        :label="__('customers.contracts.quickstart.button')"
                        :disabled="isInLoadingState"
                        @click="quickstartContract()"
                    />
                    <DButton
                        v-if="userPermissions.includes('contract.edit') && contract.status === 'draft' && contract.is_ff_child_contract === false && (contract?.parent?.status ?? null) !== 'draft'"
                        id="release_contract"
                        :label="__('customers.contracts.release.name')"
                        :disabled="isInLoadingState"
                        @click="clickRelease()"
                    />
                    <DButton
                        v-if="userPermissions.includes('contract.edit') && contract.is_pre_live && contract.has_children === false && contract.is_ff_child_contract === false"
                        id="delete_contract"
                        :label="__('customers.contracts.delete.name')"
                        :disabled="isInLoadingState"
                        @click="clickDelete()"
                    />
                    <DButton
                        v-if="userPermissions.includes('contract.restore') && isContractRestorable && contract.is_ff_child_contract === false"
                        id="restore_contract"
                        v-c-tooltip="__('customers.contracts.restore_contract.description')"
                        :label="__('customers.contracts.restore_contract.button')"
                        :disabled="isInLoadingState"
                        @click="restoreContract()"
                    />
                </template>
            </DCard>
            <DCard
                :padding="true"
                :title="__('customers.contracts.invoice_settings')"
            >
                <template #default>
                    <div class="detail">
                        <div class="two-columns">
                            <span>{{ __('customers.contracts.committed_to') }}</span>
                            <span>
                                <template
                                    v-if="contract.billed_until_date"
                                >
                                    <Link
                                        v-if="userPermissions.includes('invoice.view') && contract.billed_until_hint === false"
                                        :href="viewInvoicesUrl"
                                    >
                                        {{ setDateFormatEuropeBerlin(contract.billed_until_date) }}
                                    </Link>
                                    <span
                                        v-else-if="contract.billed_until_hint === true"
                                        v-c-tooltip="__('customers.contracts.billed_until_hint')"
                                    >
                                        {{ setDateFormatEuropeBerlin(contract.billed_until_date) }}
                                        <DIcon
                                            name="information-slab-circle-outline"
                                            :size="18"
                                        />
                                    </span>
                                    <span v-else>{{ setDateFormatEuropeBerlin(contract.billed_until_date) }}</span>
                                </template>
                                <DTag
                                    v-else
                                    :label="__('customers.contracts.no_billing')"
                                />
                                <span
                                    v-if="!isExcludedFromInvoicing()"
                                    class="font-weight-normal"
                                >

                                    ({{ contract.has_invoices ? __('customers.contracts.next_invoice') : __('customers.contracts.first_invoice') }} :
                                    {{ contract.next_invoice_date !== null ? setDateFormatEuropeBerlin(contract.next_invoice_date) : __('customers.contracts.no_next_invoice') }})
                                </span>
                                <span
                                    v-else-if="isExcludedFromInvoicing()"
                                >
                                    ({{ __('customers.contracts.invoicing_disabled') }})
                                </span>
                            </span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.price') }}</span>
                            <span v-if="contract.one_time">
                                {{ priceFormat(contract.active_contract_phase.discounted_price, 2, customer.market_region_id) }}
                            </span>
                            <span v-else-if="contract.active_contract_phase && contract.active_contract_phase.discounted_price != 0.000">
                                {{ priceFormat(contract.active_contract_phase.discounted_price, 2, customer.market_region_id) }} / {{ billingCycle(contract.active_contract_phase.billing_cycle) }}
                            </span>
                            <span v-if="contract.active_contract_phase && contract.active_contract_phase.discounted_price == 0.000">
                                <DTag :label="__('customers.contracts.inclusive')" />
                            </span>
                            <span v-if="!contract.active_contract_phase">
                                <DTag
                                    color="danger"
                                    :label="__('customers.contracts.no_contract_phases')"
                                />
                            </span>
                        </div>
                        <div>
                            <span>{{ __('customers.customers.billing_type') }}</span>
                            <span v-if="isInvoiceBeforeRenewalEnabled === true">
                                {{ __('customers.billing_types.invoice_renewal_enabled') }}
                            </span>
                            <span v-else-if="contract.billing_type">
                                {{ __('customers.billing_types.' + contract.billing_type) }}
                            </span>
                            <span v-else>
                                {{ __('customers.billing_types.' + customer.billing_type) }}
                            </span>
                        </div>
                        <div v-if="tenantSettings.supports_installments === true">
                            <span>{{ __('customers.contracts.installments') }}</span>
                            <span v-if="contract.installments === null">
                                {{ __('customers.contracts.no_installments') }}
                            </span>
                            <span v-else>
                                {{ contract.installments }} ({{ calculatedInstallments }})
                            </span>
                        </div>
                        <div
                            v-if="tenantSettings.enable_divergent_payment_methods === true"
                            class="two-columns"
                        >
                            <span>{{ __('customers.contracts.payment_method') }}</span>
                            <span class="text-space-wrap">{{ contract.payment_method }}</span>
                        </div>
                        <div
                            v-if="tenantSettings.enable_divergent_payment_methods === true"
                            class="two-columns"
                        >
                            <span>{{ __('customers.contracts.payment_term') }}</span>
                            <span class="text-space-wrap">{{ contract.payment_term }} {{ __('customers.contracts.days') }}</span>
                        </div>
                        <div
                            v-if="tenantSettings.enable_prompt_cash_discount === true"
                            class="two-columns"
                        >
                            <span>{{ __('customers.contracts.prompt_cash_discount') }}</span>
                            <span
                                v-if="contract.prompt_cash_discount_percentage !== null"
                                class="text-space-wrap"
                            >
                                {{ contract.prompt_cash_discount_days }} {{ __('customers.contracts.days') }}, {{ contract.prompt_cash_discount_percentage }}{{ '%' }}
                            </span>
                        </div>
                        <div
                            v-if="contract.invoice_text !== null && contract.invoice_text !== ''"
                            class="two-columns"
                        >
                            <span>{{ __('customers.contracts.invoice_text') }}</span>
                            <span class="text-space-wrap">{{ contract.invoice_text }}</span>
                        </div>
                    </div>
                </template>
            </DCard>
            <DCard
                v-if="contract.is_ff_child_contract === false"
                :title="__('customers.contracts.contract_phases')"
                :title-right="uncoveredFreeMonth"
                class="contract-phases"
                :footer-padding="true"
            >
                <template #default>
                    <!-- TODO: Swap this out with DTable once implemented -->
                    <table class="table">
                        <thead>
                            <tr>
                                <th>{{ __('customers.contracts.start_date') }}</th>
                                <th>{{ __('customers.contracts.product') }}</th>
                                <th>{{ __('customers.contracts.price') }}</th>
                                <th>{{ __('customers.contracts.discount') }}</th>
                                <th>{{ __('customers.contracts.notice_period') }}</th>
                                <th v-if="hasThirdPartyCost">
                                    {{ __('customers.contracts.third_party_cost') }}
                                </th>
                                <th>
                                    {{ __('customers.contracts.setup_fee') }}
                                </th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(contract_phase, index) in contract.contract_phases"
                                :key="contract_phase.id"
                                :class="contract_phase.id === contract.active_contract_phase.id ? 'table-success' : ''"
                            >
                                <td>{{ setDateFormatEuropeBerlin(contract_phase.start_date) }}</td>
                                <td :class="comparePhaseProperty(index, 'product.name')">
                                    {{ contract_phase.product.name }}
                                </td>
                                <td :class="comparePhaseProperty(index, 'discounted_price')">
                                    {{ priceFormat(contract_phase.discounted_price, 2, customer.market_region_id) }}
                                </td>
                                <td :class="comparePhaseProperty(index, 'discount_value')">
                                    <span v-if="contract_phase.discount_value !== null">
                                        {{ discountType(contract_phase.discount_type, contract_phase.discount_value) }}
                                    </span>
                                    <span v-else>
                                        {{ priceFormat(0, 2, customer.market_region_id) }}
                                    </span>
                                </td>
                                <td :class="comparePhaseProperty(index, 'notice_period') !== 'text-warning' ? comparePhaseProperty(index, 'notice_period_unit') : 'text-warning'">
                                    {{ contract_phase.notice_period }}{{ contract_phase.notice_period_unit }}
                                </td>
                                <td
                                    v-if="hasThirdPartyCost"
                                    :class="comparePhaseProperty(index, 'third_party_cost')"
                                >
                                    <span>
                                        {{ priceFormat(contract_phase.third_party_cost, 2, customer.market_region_id) }}
                                    </span>
                                </td>
                                <td :class="comparePhaseProperty(index, 'setup_fee')">
                                    <span>
                                        {{ priceFormat(contract_phase.setup_fee, 2, customer.market_region_id) }}
                                    </span>
                                </td>
                                <td>
                                    <!-- If you edit the logic of "v-if" here, please edit it also in: resources/js/Pages/Customers/ContractPhases/Create.vue "isEditable()" -->
                                    <DButton
                                        v-if="isEditableOrDeleteable(contract, contract_phase)"
                                        mini
                                        icon="pencil"
                                        :href="$route(
                                            'customers.customers.contracts.phases.edit',
                                            { customer: customer.id, contract: contract, phase: contract_phase }
                                        )"
                                    />
                                    <!-- If you edit the logic of "v-if" here, please edit it also in: resources/js/Pages/Customers/ContractPhases/Create.vue "isEditable()" -->
                                    <DButton
                                        v-if="isEditableOrDeleteable(contract, contract_phase)"
                                        mini
                                        icon="trash-can-outline"
                                        color="danger"
                                        @click="deleteContractPhase(contract_phase)"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </template>

                <template
                    #footer
                >
                    <DButton
                        :label="__('customers.contracts.details_contract_phase')"
                        :href="$route(
                            'customers.customers.contracts.phases.index',
                            { customer: customer.id, contract: contract }
                        )"
                    />
                    <DButton
                        v-if="userPermissions.includes('contract.edit') && contract.one_time !== true && contract.status === 'active'"
                        :label="__('customers.contracts.upgrade')"
                        @click="upgradeContract"
                    />
                    <DButton
                        v-if="userPermissions.includes('contract.edit') && contract.one_time !== true && contract.status === 'active'"
                        :label="__('customers.contracts.downgrade')"
                        @click="downgradeContract"
                    />
                    <DButton
                        v-if="showFreeMonthsButton"
                        :label="__('customers.contracts.free_months')"
                        @click="upgradeFreeMonths"
                    />
                    <DButton
                        v-if="showPriceOverviewButton"
                        :loading="priceOverviewLoading"
                        :label="__('customers.contracts.price_overview')"
                        @click="showPriceOverview"
                    />
                    <DButton
                        v-if="userPermissions.includes('customer.service_discount')"
                        :label="__('customers.contracts.one_time_discount.button')"
                        @click="openOneTimeDiscountModal"
                    />
                    <DButton
                        v-if="showRuntimeOverviewButton"
                        :loading="runtimeOverviewLoading"
                        :label="__('customers.contracts.runtime_overview')"
                        @click="showRuntimeOverview"
                    />
                    <DButton
                        v-if="userPermissions.includes('product.change')"
                        :class="`mt-2`"
                        :label="__('customers.contracts.product_change.title')"
                        @click="productChange"
                    />
                </template>
            </DCard>
            <ContractConnections
                v-if="connections.length > 0"
                :connections="connections"
            />
            <DCard
                class="contract-phases"
                :footer-padding="true"
            >
                <template #default>
                    <table>
                        <thead>
                            <tr>
                                <td class="detail-log-header">
                                    <span>
                                        {{ __('customers.contracts.activity_log') }}
                                    </span>
                                    <span>
                                        <Link
                                            :href="$route(
                                                'customers.customers.activity-logs.index',
                                                { customer: customer.id, subjectType: 'Contract', subjectId: contract.id }
                                            )"
                                        >
                                            ({{ logCountItems }} activities)
                                        </Link>
                                    </span>
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="logItem in logItems"
                                :key="logItem.id"
                            >
                                <td class="detail-log-td">
                                    <span class="detail-log">
                                        <span>
                                            <template v-if="logItem.user">
                                                <strong v-if="logItem.user.display_name">{{ logItem.user.display_name }} ({{ logItem.user.name }})</strong>
                                                <strong v-else>{{ logItem.user.name }}</strong>
                                            </template>
                                            <strong v-else>-</strong>
                                        </span>
                                        <span>{{ $page.props.auth.user.language_code === 'de' ? logItem.description : logItem.description_en }}</span>
                                        <span>{{ logItem.comment }}</span>
                                    </span>
                                    <span class="detail-log second-detail-log">
                                        <span>{{ logItem.created_at }}</span>
                                        <span>
                                            <DButton
                                                v-if="hasActivityLogChanges(logItem)"
                                                icon="eye"
                                                mini
                                                @click="viewChangesModal(logItem, subjectText(logItem))"
                                            />
                                        </span>
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </template>

                <template
                    #footer
                >
                    <DButton
                        :label="__('customers.contracts.details_contract_phase')"
                        :href="$route(
                            'customers.customers.activity-logs.index',
                            { customer: customer.id, subjectType: 'Contract', subjectId: contract.id }
                        )"
                    />
                </template>
            </DCard>
        </div>
        <div>
            <DCard
                :padding="true"
                :footer-padding="true"
                :title="__('customers.contracts.fulfillment_settings')"
            >
                <template
                    #default
                >
                    <div class="detail">
                        <div>
                            <span>{{ __('customers.contracts.fulfillment_strategy') }}</span>
                            <span>
                                {{ fulfillmentStrategy }}
                            </span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.fulfillment_module') }}</span>
                            <span v-if="contract.fulfillment_module">
                                {{ contract.fulfillment_module }}
                            </span>
                            <span v-else>
                                {{ __('customers.contracts.no_fulfillment_module_selected') }}
                            </span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.account') }}</span>
                            <span v-if="contract.fulfillment_account_id !== null">
                                <Link
                                    v-if="userPermissions.includes('fulfillment.view') || userPermissions.includes('fulfillment.edit')"
                                    :href="viewAccountUrl"
                                >
                                    {{ contract.fulfillment_account.name }}
                                </Link>
                                <span v-else>{{ contract.fulfillment_account.name }}</span>
                            </span>
                            <span v-else>
                                <DTag
                                    color="danger"
                                    :label="__('customers.contracts.no_contract_account')"
                                />
                            </span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.external_id') }}</span>
                            <span v-if="contract.external_id">
                                <a
                                    v-if="contract.fulfillment_account?.group?.module === 'ssl'"
                                    :href="sslToolUrl"
                                    target="_blank"
                                >
                                    {{ contract.external_id }}
                                </a>
                                <span v-else>
                                    {{ contract.external_id }}
                                </span>
                                <DButton
                                    v-c-tooltip="__('customers.contracts.copy_id_to_clipboard')"
                                    mini
                                    icon="content-copy"
                                    @click="copyToClipboard(contract.external_id)"
                                />
                            </span>
                            <DTag
                                v-else
                                :label="__('customers.contracts.unknown_external_id')"
                            />
                        </div>
                        <div v-if="userPermissions.includes('contract.dns_provider.edit') && contract.domain?.dns_provider">
                            <span>{{ __('customers.contracts.dns_provider') }}</span>
                            <span>{{ contract.domain.dns_provider.name }}</span>
                        </div>
                        <div v-if="vmMonitoringUrl">
                            <span>{{ __('customers.contracts.vm_monitoring') }}</span>
                            <span>
                                <a
                                    :href="vmMonitoringUrl"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Grafana
                                </a>
                            </span>
                        </div>
                    </div>
                </template>
                <template
                    v-if="userPermissions.includes('customer.edit') && contract.supports_sync"
                    #footer
                >
                    <DButton
                        :label="__('messages.actions.one_home_contract_sync')"
                        @click="$inertia.post($route('customers.customers.contracts.sync_one_home', { customer: customer.id, contract: contract.id }))"
                    />
                </template>
            </DCard>
            <DCard
                v-if="contract.type === 'webhosting'"
                :title="__('customers.contracts.details')"
                :padding="true"
                :footer-padding="true"
            >
                <template
                    #default
                >
                    <div class="detail">
                        <div>
                            <span>{{ __('customers.contracts.details_domain') }}</span>
                            <span>{{ contract.external_id }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.details_username') }}</span>
                            <span>{{ username }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.details_server') }}</span>
                            <span>{{ fulfillmentAccount ? fulfillmentAccount.hostname : '' }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.details_ipv4') }}</span>
                            <span>{{ ipv4 }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.inclusive_domains.possible_inclusive_domains') }}</span>
                            <div class="pt-2">
                                <p
                                    v-for="(value, index) in possibleInclusiveDomains"
                                    :key="index"
                                >
                                    {{ value }}
                                </p>
                            </div>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.inclusive_domains.used_inclusive_domains') }}</span>
                            <div class="pt-2">
                                <p
                                    v-for="(value, index) in usedInclusiveDomains"
                                    :key="index"
                                >
                                    {{ value }}
                                </p>
                            </div>
                        </div>
                    </div>
                </template>
                <template
                    #footer
                >
                    <DButton
                        v-if="showChangePleskServerButton"
                        :label="__('customers.contracts.change_plesk_server.change_plesk_server')"
                        @click="viewChangePleskModule"
                    />
                    <DButton
                        v-if="showAntagonistSuspensionFormButton"
                        :label="__('customers.contracts.antagonist_suspension_button.track_suspension')"
                        @click="$inertia.get($route('customers.customers.contracts.track-suspension', {
                            customer: customer.id,
                            contract: contract.id
                        }))"
                    />
                </template>
            </DCard>
            <DCard
                v-if="contract.fulfillment_module === 'webcard'"
                :title="__('customers.contracts.details')"
                :padding="true"
                :footer-padding="true"
            >
                <template
                    #default
                >
                    <div class="detail">
                        <div>
                            <span>{{ __('customers.contracts.inclusive_domains.possible_inclusive_domains') }}</span>
                            <div class="pt-2">
                                <p
                                    v-for="(value, index) in possibleInclusiveDomains"
                                    :key="index"
                                >
                                    {{ value }}
                                </p>
                            </div>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.inclusive_domains.used_inclusive_domains') }}</span>
                            <div class="pt-2">
                                <p
                                    v-for="(value, index) in usedInclusiveDomains"
                                    :key="index"
                                >
                                    {{ value }}
                                </p>
                            </div>
                        </div>
                    </div>
                </template>
                <template
                    #footer
                >
                    <DButton
                        v-if="showChangePleskServerButton"
                        :label="__('customers.contracts.change_plesk_server.change_plesk_server')"
                        @click="viewChangePleskModule"
                    />
                </template>
            </DCard>
            <DCard
                :title="__('customers.contracts.technical_details')"
                :padding="true"
                :footer-padding="true"
                :hint-right="contract.configValuesSortingType ? __(`customers.contracts.sorting.${contract.configValuesSortingType}`) : ''"
            >
                <template
                    #default
                >
                    <div class="config-option-details">
                        <p
                            v-if="infoMessage"
                            class="alert alert-warning info-message"
                        >
                            {{ __(infoMessage) }}
                        </p>

                        <!-- Instance config options -->
                        <ConfigOptionList
                            :options="instanceConfigOptions"
                            :contract="contract"
                            :is-domain-transfer="isDomainTransfer"
                            @restartTransfer="restartTransfer"
                        />
                        <span
                            v-if="Object.values(instanceConfigOptions).length > 0"
                            class="cutting-line"
                        />

                        <!-- Contract config options -->
                        <ConfigOptionList
                            :options="contractConfigOptions"
                            :contract="contract"
                            :is-domain-transfer="isDomainTransfer"
                            @restartTransfer="restartTransfer"
                        />
                        <span
                            v-if="Object.values(contractConfigOptions).length > 0"
                            class="cutting-line"
                        />

                        <!-- Product config options -->
                        <ConfigOptionList
                            :options="productConfigOptions"
                            :contract="contract"
                            :is-domain-transfer="isDomainTransfer"
                            @restartTransfer="restartTransfer"
                        />
                    </div>
                </template>

                <template
                    #footer
                >
                    <span
                        v-if="hasSSO && contract.fulfillment_account !== null && contract.status !== 'draft'"
                    >
                        <SsoButtons
                            :fulfillment-account="contract.fulfillment_account"
                            :sso-types="ssoTypes"
                            @navigateToSSO="navigateToSSO"
                        />
                    </span>
                    <span
                        v-if="userPermissions.includes('contract.fulfillment') && contract.supports_dns_sec === true && contract.status === 'active'"
                    >
                        <DDropdown
                            id="dns_sec_dropdown"
                            :toggler-text="__('customers.contracts.dns_sec.dns_sec')"
                            class="d-inline-flex"
                        >
                            <DDropdownItem
                                id="dns_sec_renew"
                                @click="renewDNSSec(true)"
                            >
                                {{ __('customers.contracts.dns_sec.dns_sec_renew') }}
                            </DDropdownItem>
                            <DDropdownItem
                                id="dns_sec_re_enable_keys"
                                @click="renewDNSSec(false)"
                            >
                                {{ __('customers.contracts.dns_sec.dns_sec_re_enable') }}
                            </DDropdownItem>
                            <DDropdownItem
                                id="dns_sec_disable"
                                @click="disableDNSSec()"
                            >
                                {{ __('customers.contracts.dns_sec.dns_sec_disable') }}
                            </DDropdownItem>
                        </DDropdown>
                    </span>
                    <span
                        v-if="showMoveZoneButton"
                    >
                        <DButton
                            :label="__('customers.contracts.move_zone.move_zone_button')"
                            @click="displayMoveZoneModal"
                        />
                    </span>
                    <span>
                        <DButton
                            v-if="showResetButton"
                            :label="__('customers.contracts.reset_button')"
                            @click="resetContract"
                        />
                    </span>
                    <span>
                        <DButton
                            v-if="showSSLReissueButton"
                            :label="__('customers.contracts.reissue_ssl_button')"
                            @click="reissueSSLCertificate"
                        />
                    </span>
                </template>
            </DCard>
            <DCard
                :title="__('customers.contracts.notes.internal')"
                :padding="true"
                footer-padding
            >
                <template #default>
                    <p
                        v-for="(note, noteIndex) in notesInternal"
                        :key="noteIndex"
                    >
                        {{ note }}
                    </p>
                </template>

                <template
                    v-if="userPermissions.includes('contract.comment') || userPermissions.includes('contract.edit')"
                    #footer
                >
                    <DButton
                        mini
                        :label="__('customers.contracts.notes.edit')"
                        @click="showEditNoteModal = true"
                    />
                </template>
            </DCard>
            <DCard
                :title="__('customers.contracts.notes.invoice')"
                :padding="true"
                footer-padding
            >
                <template #default>
                    <div>
                        <p
                            v-for="(note_invoice_paragraph, index) in nl2array(contract.notes_invoice)"
                            :key="index"
                        >
                            {{ note_invoice_paragraph }}
                        </p>
                    </div>
                </template>

                <template
                    v-if="userPermissions.includes('contract.comment') || userPermissions.includes('contract.edit')"
                    #footer
                >
                    <DButton
                        mini
                        :label="__('customers.contracts.notes.edit')"
                        @click="showEditNoteModal = true"
                    />
                </template>
            </DCard>
            <DCard
                :title="__('customers.customers.files.title')"
                footer-padding
            >
                <template #default>
                    <div
                        v-for="customerFile in customerFiles"
                        :key="customerFile.id"
                        class="file"
                    >
                        <DIcon
                            name="file"
                            :size="32"
                        />
                        <div class="meta">
                            <div class="filename">
                                {{ customerFile.original_name }}
                            </div>
                            <div class="description">
                                {{ customerFile.description }}
                            </div>
                            <div class="meta">
                                {{ new Date(customerFile.created_at).toLocaleDateString() }} &bull; {{ customerFile.user_name }}
                            </div>
                        </div>
                        <DButton
                            icon="download"
                            :disabled="isInLoadingState"
                            @click="downloadContractFile(customerFile.id)"
                        />
                    </div>
                    <div
                        v-if="customerFiles.length === 0"
                        class="no-files"
                    >
                        {{ __('customers.customers.files.no_files_contract') }}
                    </div>
                </template>

                <template #footer>
                    <DButton
                        v-if="userPermissions.includes('customer.view')"
                        mini
                        :label="__('customers.customers.files.footer.all_files')"
                        @click="$inertia.get($route('customers.customers.customer-files.index', {customer: customer.id}))"
                    />
                </template>
            </DCard>
            <DCard
                :title="__('customers.contracts.domain_contact_details.title')"
                padding
                footer-padding
            >
                <template #default>
                    <div
                        v-if="contract.supports_owner_data === false"
                        class="no-files"
                    >
                        {{ __('customers.contracts.domain_contact_details.owner_data_not_supported') }}
                    </div>
                    <div
                        v-else-if="contract.domain_owner === null"
                        class="no-files"
                    >
                        {{ __('customers.contracts.domain_contact_details.missing_domain_contact_details') }}
                    </div>
                    <div
                        v-else-if="contract.domain_owner.uses_customer_data"
                        class="no-files"
                    >
                        {{ __('customers.contracts.domain_contact_details.uses_customer_data') }}
                    </div>
                    <div
                        v-else
                        class="detail"
                    >
                        <div>
                            <span>{{ __('customers.contracts.domain_contact_details.firstname') }}</span>
                            <span>{{ contract.domain_owner.firstname ?? '' }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.domain_contact_details.lastname') }}</span>
                            <span>{{ contract.domain_owner.lastname ?? '' }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.domain_contact_details.company') }}</span>
                            <span>{{ contract.domain_owner.company ?? '' }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.domain_contact_details.email') }}</span>
                            <span>{{ contract.domain_owner.email ?? '' }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.domain_contact_details.phone') }}</span>
                            <span>{{ contract.domain_owner.phone ?? '' }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.domain_contact_details.fax') }}</span>
                            <span>{{ contract.domain_owner.fax ?? '' }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.domain_contact_details.address1') }}</span>
                            <span>{{ contract.domain_owner.address1 ?? '' }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.domain_contact_details.address2') }}</span>
                            <span>{{ contract.domain_owner.address2 ?? '' }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.domain_contact_details.postal_code') }}</span>
                            <span>{{ contract.domain_owner.postal_code ?? '' }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.domain_contact_details.city') }}</span>
                            <span>{{ contract.domain_owner.city ?? '' }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.domain_contact_details.country_code') }}</span>
                            <span>{{ contract.domain_owner.country_code ?? '' }}</span>
                        </div>
                        <div
                            v-for="field in contract.domain_owner.custom_fields ?? []"
                            :key="field.key"
                        >
                            <span>{{ field.key }}</span>
                            <span>{{ field.value }}</span>
                        </div>
                        <div>
                            <span>{{ __('customers.contracts.domain_contact_details.status') }}</span>
                            <span>
                                <DBadge
                                    v-if="contract.domain_owner.pending_update === true"
                                    color="danger"
                                >
                                    {{ __('customers.contracts.domain_contact_details.update_pending') }}
                                </DBadge>
                                <DBadge
                                    v-else
                                    color="success"
                                >
                                    {{ __('customers.contracts.domain_contact_details.current') }}
                                </DBadge>
                            </span>
                        </div>
                    </div>
                </template>

                <template #footer>
                    <DButton
                        v-if="areDomainOwnerActionsEnabled && contract.status === 'active'"
                        mini
                        icon="cog-outline"
                        :label="__('customers.contracts.domain_contact_details.trigger_owner_change')"
                        @click="triggerOwnerChange"
                    />
                    <DButton
                        v-if="areDomainOwnerActionsEnabled && contract.status === 'active' && contract.domain_owner === null"
                        mini
                        icon="sync"
                        :label="__('customers.contracts.domain_contact_details.synchronize_owner_data')"
                        @click="synchronizeOwnerData"
                    />
                    <DButton
                        v-if="areDomainOwnerActionsEnabled"
                        mini
                        icon="pencil"
                        :label="__('customers.contracts.domain_contact_details.edit')"
                        @click="showEditOwnerDataModal"
                    />
                    <DButton
                        v-if="contract.type === 'domain' && contract.status === 'active' && tenantSettings.manual_domain_email_verification === true"
                        mini
                        icon="email-check-outline"
                        :label="__('customers.contracts.domain_contact_details.manual_email_verification')"
                        @click="triggerEmailVerification"
                    />
                    <DButton
                        v-if="isVerificationEmailWaitForInput"
                        mini
                        icon="email-check-outline"
                        :label="__('customers.contracts.domain_contact_details.resend_verification_email')"
                        @click="resendEmailVerificationEmail"
                    />
                    <DButton
                        v-if="isVerificationEmailWaitForInput"
                        id="force_fulfill_without_email_verification"
                        mini
                        icon="email-check-outline"
                        :label="__('customers.contracts.domain_contact_details.fulfill_without_email_check')"
                        @click="showForceFulfillWithoutEmailVerificationModal = true"
                    />
                </template>
            </DCard>

            <DCard
                v-if="supportLinks.length > 0"
                :title="__('customers.contracts.support_links.title')"
                padding
            >
                <template #default>
                    <div class="detail">
                        <div
                            v-for="(link, index) in supportLinks"
                            :key="index"
                        >
                            <span>{{ __('customers.contracts.support_links.types.' + link.type) }}</span>
                            <span>
                                <a
                                    :href="link.url"
                                    target="_blank"
                                >
                                    {{ link.label }}
                                </a>
                            </span>
                        </div>
                    </div>
                </template>
            </DCard>
        </div>

        <DFabs>
            <s-s-o-fab
                v-if="contract.status !== 'draft'"
                :customer="customer"
                @open-modal="showCreateCloudpitCustomerModal = true"
                @close-modal="showCreateCloudpitCustomerModal = false"
            />
            <DFab
                v-if="isPartnerContract && userPermissions.includes('contract.partners') && !isProviderboxContract"
                icon="eye"
                :label="__('customers.contracts.partner_contracts.show_partner_contracts')"
                @click="viewPartnerContracts"
            />
            <DFab
                v-if="isResellerContract && !isPartnerContract"
                icon="eye"
                :label="__('customers.contracts.reseller_contracts.view_reseller_contracts')"
                @click="viewResellerContracts"
            />
            <DFab
                v-if="isProviderboxContract && userPermissions.includes('providerbox.import')"
                icon="eye"
                :label="__('customers.contracts.reseller_contracts.view_providerbox_contracts')"
                @click="viewProviderboxContracts"
            />
            <DFab
                v-if="userPermissions.includes('contract.edit') && contract.status !== 'terminated' && contract.status !== 'cancelled'"
                id="btn-edit"
                icon="pencil"
                :label="__('messages.actions.edit')"
                @click="$inertia.get($route('customers.customers.contracts.edit', {
                    customer: customer.id,
                    contract: contract.id
                }))"
            />
        </DFabs>

        <DModal
            v-if="contract.status !== 'terminated' && contract.status !== 'cancelled'"
            v-model:show="showSSOErrorModal"
            :title="__('customers.contracts.sso_error_title')"
            :message="ssoErrorMessage"
            is-error
            @close="showSSOErrorModal = false"
        />

        <ProductUpgradeModal
            v-if="showUpgradeModal"
            :contract="contract"
            :errors-data="errors"
            :show-modal="showUpgradeModal"
            :active-product-id="activeProductId"
            :discount-types="discountTypes"
            :market-region-id="customer.market_region_id"
            @close-modal="closeUpgrade"
        />
        <EditNoteModal
            :contract="contract"
            :show-modal="showEditNoteModal"
            :errors-data="errors"
            @close-modal="showEditNoteModal = false"
        />
        <ActivateContractModal
            v-if="showActivateModal"
            :contract="contract"
            :show-modal="showActivateModal"
            :redirect-to="redirectToWhenActivate"
            @close-modal="showActivateModal = false"
        />
        <TerminateContractModal
            v-if="showTerminationModal"
            :contract="contract"
            :show-modal="showTerminationModal"
            :termination-reasons="terminationReasons"
            :termination-dates="terminationDates"
            :errors-data="errors"
            redirect-to="contract-show"
            @close-modal="showTerminationModal = false"
            @update-termination-dates="updateTerminationDates"
        />
        <CancelContractTerminationModal
            v-if="showConfirmationModal"
            :contract="contract"
            :show-modal="showConfirmationModal"
            redirect-to="contract-show"
            @close-modal="showConfirmationModal = false"
        />
        <TerminateSilentlyModal
            v-if="showTerminateSilentlyModal"
            :contract="contract"
            :show-modal="showTerminateSilentlyModal"
            redirect-to="contract-show"
            @close-modal="showTerminateSilentlyModal = false"
        />
        <MoneyBackGuaranteeModal
            v-if="showGuaranteeConfirmationModal"
            :contract="contract"
            :show-modal="showGuaranteeConfirmationModal"
            redirect-to="contract-show"
            @close-modal="showGuaranteeConfirmationModal = false"
        />
        <WithdrawContractModal
            v-if="showWithdrawConfirmationModal"
            :contract="contract"
            :show-modal="showWithdrawConfirmationModal"
            redirect-to="contract-show"
            @close-modal="showWithdrawConfirmationModal = false"
        />
        <QuickstartContractModal
            :contract="contract"
            :show-modal="showQuickstartConfirmationModal"
            @close-modal="showQuickstartConfirmationModal = false"
        />
        <MoveContractModal
            v-if="showMoveContractModal"
            :contract="contract"
            :show-modal="showMoveContractModal"
            redirect-to="contract-show"
            @close-modal="showMoveContractModal = false"
        />
        <CreateCloudPitCustomerModal
            :customer="customer"
            :show-modal="showCreateCloudpitCustomerModal"
            @close-modal="showCreateCloudpitCustomerModal = false"
        />
        <DModal
            v-model:show="showContractPhaseDeleteConfirmationModal"
            :title="__('contracts.contractPhases.delete_confirmation.title')"
            :message="__('contracts.contractPhases.delete_confirmation.message')"
            is-confirm
            is-error
            @close="showContractPhaseDeleteConfirmationModal = false"
            @confirm="deleteContractPhaseCallBack"
        />
        <FreeMonthsModal
            v-if="showFreeMonthsModal"
            :contract="contract"
            :show-modal="showFreeMonthsModal"
            :errors-data="errors"
            @close-modal="closeFreeMonths"
        />
        <OneTimeDiscountModal
            v-if="showOneTimeDiscountModal"
            :contract="contract"
            :show-modal="showOneTimeDiscountModal"
            :discount-available="oneTimeDiscountAvailable"
            :max-percentage="oneTimeDiscountMaxPercentage"
            :errors-data="errors"
            @close-modal="showOneTimeDiscountModal = false"
        />
        <PriceOverviewModal
            v-if="showPriceOverviewModal"
            :contract="contract"
            :grouped-contract-phases="groupedContractPhases"
            :discount-types="discountTypes"
            :billing-cycles="billingCycles"
            :show-modal="showPriceOverviewModal"
            :tax-rate="taxRate"
            :errors-data="errors"
            :customer="customer"
            @close-modal="closePriceOverview"
        />
        <RuntimeOverviewModal
            v-if="showRuntimeOverviewModal"
            :contract="contract"
            :grouped-contract-phases="groupedContractPhases"
            :units="contractTermUnits"
            :show-modal="showRuntimeOverviewModal"
            :errors-data="errors"
            @close-modal="closeRuntimeOverview"
        />
        <DowngradeContractModal
            v-if="showDowngradeModal"
            :contract="contract"
            :show-modal="showDowngradeModal"
            :active-product-id="activeProductId"
            :errors-data="errors"
            :market-region-id="customer.market_region_id"
            @close-modal="closeDowngrade"
        />
        <ChangePleskServerForm
            v-if="showChangePleskModuleModal"
            :contract="contract"
            :fulfillment-account="fulfillmentAccount"
            :show="showChangePleskModuleModal"
            :errors-data="errors"
            @close-modal="showChangePleskModuleModal = false"
        />
        <UnsuspendContractModal
            :contract="contract"
            :show-modal="showUnsuspendConfirmationModal"
            :redirect-to="'contract-show'"
            @close-modal="showUnsuspendConfirmationModal = false"
        />
        <AbuseLockContractModal
            :contract="contract"
            :show-modal="showAbuseLockModal"
            :enabled="abuseLockEnable"
            @close-modal="showAbuseLockModal = false"
        />
        <ReleaseContractModal
            :contract="contract"
            :show-modal="showReleaseConfirmationModal"
            :redirect-to="'contract-show'"
            @close-modal="showReleaseConfirmationModal = false"
        />
        <DeleteContractModal
            :contract="contract"
            :show-modal="showDeleteConfirmationModal"
            :message="deleteModalMessage"
            @close-modal="showDeleteConfirmationModal = false"
        />
        <RestartDomainTransferModal
            :auth-code="authCode"
            :show-modal="showRestartTransferModal"
            :contract="contract"
            @close-modal="showRestartTransferModal = false"
        />
        <ProductChangeModal
            v-if="showProductChangeModal"
            :show-modal="showProductChangeModal"
            :contract="contract"
            :customer="customer"
            @close-modal="closeProductChange"
        />
        <EditDomainContactDetailsModal
            v-if="showEditDomainContactDataModal"
            :show-modal="showEditDomainContactDataModal"
            :contract="contract"
            :customer="customer"
            :phone-country-code-placeholder="phoneCountryCodePlaceholder"
            @close-modal="hideEditOwnerDataModal"
        />
        <DModal
            v-model:show="showDisabledMoveContractModal"
            :title="__('messages.warning')"
            :message="__('customers.contracts.move_contract.disabled')"
            is-error
            @close="showDisabledMoveContractModal = false"
        />
        <RestoreContract
            v-if="showRestoreContractModal"
            v-model:showModal="showRestoreContractModal"
            :contract="contract"
            redirect-to="show"
            @close-modal="showRestoreContractModal = false"
        />
        <ForceEmailVerification
            v-if="showForceFulfillWithoutEmailVerificationModal"
            v-model:showModal="showForceFulfillWithoutEmailVerificationModal"
            :contract="contract"
            @close-modal="showForceFulfillWithoutEmailVerificationModal = false"
        />
        <SendAuthCodeModal
            v-if="showSendAuthCodeModal"
            :contract="contract"
            :show="showSendAuthCodeModal"
            :errors-data="errors"
            @close-modal="showSendAuthCodeModal = false"
        />
    </div>
    <ChangesModal
        v-if="showChangesModal"
        :show-modal="showChangesModal"
        :changes="changes"
        :subject="subject"
        @close-modal="showChangesModal = false"
    />
    <MoveZoneModal
        :show="showMoveZoneModal"
        :contract="contract"
        :one-hop-brand="oneHopBrand"
        @close-modal="showMoveZoneModal = false"
    />
</template>

<script>
import CustomerBase from '~@/Pages/Customers/Base'
import App from '~@/App.vue'
import billingCycle from '~@/Mixins/billingCycle'
import nl2array from '~@/Mixins/nl2array'
import orderNumber from '~@/Mixins/orderNumber'

import ProductUpgradeModal from '~@/Components/Customers/Contracts/Modals/ProductUpgrade'
import EditNoteModal from '~@/Components/Customers/Contracts/Modals/EditNote'
import TerminateContractModal from '~@/Components/Customers/Contracts/Modals/TerminateContract'
import CancelContractTerminationModal from '~@/Components/Customers/Contracts/Modals/CancelContractTermination'
import TerminateSilentlyModal from '~@/Components/Customers/Contracts/Modals/TerminateSilently'
import MoneyBackGuaranteeModal from '~@/Components/Customers/Contracts/Modals/MoneyBackGuarantee'
import WithdrawContractModal from '~@/Components/Customers/Contracts/Modals/WithdrawContract'
import QuickstartContractModal from '~@/Components/Customers/Contracts/Modals/QuickstartContract'
import MoveContractModal from '~@/Components/Customers/Contracts/Modals/MoveContract'
import SSOFab from '~@/Components/Customers/SSOFab'
import CreateCloudPitCustomerModal from '~@/Components/Customers/CreateCloudPitCustomerModal'
import FreeMonthsModal from '~@/Components/Customers/Contracts/Modals/FreeMonths'
import PriceOverviewModal from '~@/Components/Customers/Contracts/Modals/PriceOverview'
import DowngradeContractModal from '~@/Components/Customers/Contracts/Modals/DowngradeContract'
import ChangePleskServerForm from '~@/Components/Customers/Contracts/Modals/ChangePleskServerForm'
import UnsuspendContractModal from '~@/Components/Customers/Contracts/Modals/UnsuspendContractModal'
import AbuseLockContractModal from '~@/Components/Customers/Contracts/Modals/AbuseLockContractModal'
import ReleaseContractModal from '~@/Components/Customers/Contracts/Modals/ReleaseContractModal'
import DeleteContractModal from '~@/Components/Customers/Contracts/Modals/DeleteContractModal'
import RestartDomainTransferModal from '~@/Components/Customers/Contracts/Modals/RestartDomainTransfer'
import ActivateContractModal from '~@/Components/Customers/Contracts/Modals/ActivateContract.vue'
import ProductChangeModal from '~@/Components/Customers/Contracts/Modals/ProductChange.vue'
import RuntimeOverviewModal from '~@/Components/Customers/Contracts/Modals/RuntimeOverview.vue'
import priceFormat from '~@/Mixins/priceFormat'
import EditDomainContactDetailsModal
    from '~@/Components/Customers/Contracts/Modals/EditDomainContactDetailsModal.vue'
import { __ } from '~@/Composables/translation'
import IncidentFlag from '~@/Components/Customers/BusinessIncidents/IncidentFlag'
import ConfigOptionList from '~@/Components/Customers/Contracts/ConfigOptionList.vue'
import SsoButtons from '~@/Components/Customers/Contracts/SsoButtons.vue'
import ChangesModal from '~@/Components/Common/ActivityLogs/ChangesModal.vue'
import { activityLogChanges, hasActivityLogChanges } from '~@/Composables/activityLogChanges'
import MoveZoneModal from '~@/Components/Customers/Contracts/Modals/MoveZoneModal'
import OneTimeDiscountModal from '~@/Components/Customers/Contracts/Modals/OneTimeDiscountModal'
import RestoreContract from '~@/Components/Customers/Contracts/Modals/RestoreContract.vue'
import SendAuthCodeModal from '~@/Components/Customers/Contracts/Modals/SendAuthCode.vue'
import ForceEmailVerification from '~@/Components/Customers/Contracts/Modals/ForceEmailVerification.vue'
import ContractConnections from '~@/Components/Customers/Contracts/ContractConnections.vue'

export default {
    name: 'CustomerContractShow',
    components: {
        ContractConnections,
        RestoreContract,
        ForceEmailVerification,
        ChangesModal,
        ConfigOptionList,
        EditDomainContactDetailsModal,
        ActivateContractModal,
        RuntimeOverviewModal,
        DeleteContractModal,
        ReleaseContractModal,
        UnsuspendContractModal,
        AbuseLockContractModal,
        CreateCloudPitCustomerModal,
        SSOFab,
        SsoButtons,
        ProductUpgradeModal,
        EditNoteModal,
        TerminateContractModal,
        CancelContractTerminationModal,
        TerminateSilentlyModal,
        MoneyBackGuaranteeModal,
        WithdrawContractModal,
        QuickstartContractModal,
        MoveContractModal,
        FreeMonthsModal,
        PriceOverviewModal,
        DowngradeContractModal,
        ChangePleskServerForm,
        RestartDomainTransferModal,
        ProductChangeModal,
        IncidentFlag,
        MoveZoneModal,
        OneTimeDiscountModal,
        SendAuthCodeModal
    },
    mixins: [
        billingCycle,
        nl2array,
        orderNumber,
        priceFormat
    ],
    layout: [App, CustomerBase],
    props: {
        contract: {
            type: Object,
            default: () => { return {} }
        },
        connections: {
            type: Array,
            default: () => { return [] }
        },
        hasSSO: {
            type: Boolean,
            default: () => { return false }
        },
        ssoTypes: {
            type: Array,
            default: () => { return [] }
        },
        customer: {
            type: Object,
            default: () => { return {} }
        },
        fulfillmentAccount: {
            type: Object,
            default: () => { return {} }
        },
        errors: {
            type: Object,
            default: () => { return {} }
        },
        terminationReasons: {
            type: Array,
            default: () => { return [] }
        },
        activeProductId: {
            type: Number,
            default: 0
        },
        discountTypes: {
            type: Array,
            default: () => { return [] }
        },
        contractTermUnits: {
            type: Array,
            default: () => { return [] }
        },
        billingCycles: {
            type: Array,
            default: () => { return [] }
        },
        taxRate: {
            type: Number,
            default: () => { return 0 }
        },
        customerFiles: {
            type: Array,
            default: () => { return [] }
        },
        isResellerContract: {
            type: Boolean,
            default: () => { return false }
        },
        isPartnerContract: {
            type: Boolean,
            default: () => { return false }
        },
        isProviderboxContract: {
            type: Boolean,
            default: () => { return false }
        },
        infoMessage: {
            type: String,
            default: () => { return null }
        },
        domainToolUrl: {
            type: String,
            default: null
        },
        vmMonitoringUrl: {
            type: String,
            default: null
        },
        isContractRestorable: {
            type: Boolean,
            default: () => { return false }
        },
        phoneCountryCodePlaceholder: {
            type: String,
            default: ''
        },
        logCountItems: {
            type: Number,
            default: 0
        },
        logItems: {
            type: Object,
            default: () => { return {} }
        },
        oneHopBrand: {
            type: String,
            default: null
        },
        supportLinks: {
            type: Array,
            default: () => { return [] }
        },
        isInvoiceBeforeRenewalEnabled: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            showSSOErrorModal: false,
            showUpgradeModal: false,
            showTerminateSilentlyModal: false,
            ssoErrorMessage: '',
            selectedContract: null,
            selectedCustomer: null,
            showConfirmationModal: false,
            contractTermination: null,
            showTerminationModal: false,
            showEditNoteModal: false,
            showGuaranteeConfirmationModal: false,
            showWithdrawConfirmationModal: false,
            showQuickstartConfirmationModal: false,
            showMoveContractModal: false,
            terminationDates: [],
            showCreateCloudpitCustomerModal: false,
            showContractPhaseDeleteConfirmationModal: false,
            showFreeMonthsModal: false,
            showMoveZoneModal: false,
            groupedContractPhases: [],
            showPriceOverviewModal: false,
            priceOverviewLoading: false,
            showRuntimeOverviewModal: false,
            runtimeOverviewLoading: false,
            deleteContractPhaseCallBack: () => {},
            showDowngradeModal: false,
            showChangePleskModuleModal: false,
            showUnsuspendConfirmationModal: false,
            showAbuseLockModal: false,
            abuseLockEnable: false,
            showReleaseConfirmationModal: false,
            showDeleteConfirmationModal: false,
            deleteModalMessage: null,
            showRestartTransferModal: false,
            showActivateModal: false,
            redirectToWhenActivate: 'contract-show',
            showProductChangeModal: false,
            showDisabledMoveContractModal: false,
            showRestoreContractModal: false,
            showForceFulfillWithoutEmailVerificationModal: false,
            modalAcceptCallback: () => { return {} },
            showEditDomainContactDataModal: false,
            showSendAuthCodeModal: false,
            showChangesModal: false,
            showOneTimeDiscountModal: false,
            oneTimeDiscountAvailable: false,
            oneTimeDiscountMaxPercentage: 100,
            subject: null,
            changes: {}
        }
    },
    computed: {
        canTerminate () {
            return !this.contract.termination &&
                this.userPermissions.includes('contract.terminate') &&
                this.contract.allowed_to_terminate
        },
        canCancelTermination () {
            return this.contract.termination &&
                this.userPermissions.includes('contract.terminate') &&
                this.contract.active_termination_status !== 'done' &&
                this.contract.termination_cancellation_possible === true
        },
        canApplyMoneyBackOrWithdraw () {
            return !this.contract.termination &&
                this.userPermissions.includes('contract.terminate') &&
                this.contract.manual_termination_disallowed !== true
        },
        hasTerminationActions () {
            return Boolean(this.canTerminate || this.canCancelTermination || this.canApplyMoneyBackOrWithdraw || this.canTerminateSilently)
        },
        canActivate () {
            return this.userPermissions.includes('contract.pending')
        },
        canTerminateSilently () {
            return !this.contract.one_time &&
                this.contract.status !== 'draft' &&
                this.contract.status !== 'scheduled' &&
                this.userPermissions.includes('contract.terminate_silently') &&
                this.contract.active_termination_status !== 'done' &&
                this.contract.status !== 'cancelled' &&
                this.contract.allowed_to_terminate &&
                this.contract.is_ff_child_contract === false &&
                this.contract.silent_termination_supported
        },
        ipv4 () {
            const contractConfigValues = this.contract.fulfillment_contract_config_values
            const ipv4ConfigValue = contractConfigValues
                .filter((configValue) => configValue.key === 'ip4_address')
                .pop()
            if (!ipv4ConfigValue) {
                return '-'
            }

            return ipv4ConfigValue.value
        },
        username () {
            const contractConfigValues = this.contract.fulfillment_contract_config_values
            const userConfigValue = contractConfigValues
                .filter((configValue) => configValue.key === 'username')
                .pop()
            if (!userConfigValue) {
                return '-'
            }

            return userConfigValue.value
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        authCode () {
            const contractConfigValues = this.contract.fulfillment_contract_config_values
            const authCodeConfigValue = contractConfigValues
                .filter((configValue) => configValue.key === 'authcode')
                .pop()
            if (!authCodeConfigValue) {
                return ''
            }
            return authCodeConfigValue.value
        },
        activeContractPhaseInfo () {
            if (!this.contract.active_contract_phase) return ''

            return this.contract.active_contract_phase.contract_term +
                ' ' + this.__('messages.contract_term_units.' + this.contract.active_contract_phase.contract_term_unit) +
                ' (' + this.contract.active_contract_phase.notice_period +
                ' ' + this.__('messages.contract_term_units.' + this.contract.active_contract_phase.notice_period_unit) +
                ')'
        },
        viewInvoicesUrl () {
            return this.$route('customers.customers.invoices.index', {
                customer: this.contract.customer_id,
                contract: this.contract.id
            })
        },
        viewOrderUrl () {
            return this.$route('orders.details', this.orderDetailsRouteParams(this.contract.order))
        },
        viewProductUrl () {
            const route = this.contract.active_contract_phase.product.is_addon ? 'products.product-addons.show' : 'products.products.show'

            return this.$route(route, {
                product: this.contract.active_contract_phase.product.id
            })
        },
        viewParentContractUrl () {
            return this.$route('customers.customers.contracts.show', {
                customer: this.customer.id,
                contract: this.contract.parent.id
            })
        },
        viewAccountUrl () {
            return this.$route('products.accounts.show', {
                account: this.contract.fulfillment_account_id
            })
        },
        viewFulfillmentRequestUrl () {
            return this.$route('tools.fulfillment.index', {
                contract: this.contract.id,
                reset: 0
            })
        },
        sslToolUrl () {
            return this.contract.fulfillment_account.hostname + '/certificates?search=' + this.contract.external_id + '&order_by=id'
        },
        orderNumber () {
            // Internal orders resolve to "<internal id> - Internal", so there is no empty case left.
            return this.contract.order.order_number_with_client
        },
        productName () {
            if (this.contract.fulfillment_module !== 'externaldomain') {
                return this.contract.active_contract_phase.product.name
            } else {
                return this.__('customers.contracts.external_domain') + ' (' + this.contract.active_contract_phase.product.name + ')'
            }
        },
        notesInternal () {
            return this.contract.notes_internal?.split(/\n/)
        },
        showFreeMonthsButton () {
            return this.contract.one_time !== true &&
                this.contract.status !== 'terminated' &&
                this.contract.status !== 'cancelled' &&
                (
                    this.userPermissions.includes('contract.edit') ||
                    this.userPermissions.includes('contract.free_months_unlimited')
                )
        },
        showPriceOverviewButton () {
            return this.contract.one_time !== true && this.userPermissions.includes('price.edit')
        },
        showRuntimeOverviewButton () {
            return this.contract.one_time !== true && this.userPermissions.includes('contract.edit')
        },
        showChangePleskServerButton () {
            return this.userPermissions.includes('contract.edit') && this.contract.fulfillment_module === 'plesk'
        },
        showResetButton () {
            return this.contract.status === 'active' && this.contract.supports_reset === true && ['yext', 'herold_at'].includes(this.contract.fulfillment_module)
        },
        showSSLReissueButton () {
            return this.contract.fulfillment_module === 'ssl'
        },
        isVpsContract () {
            return ['virtual_server', 'premium_vps'].includes(this.contract.fulfillment_module)
        },
        isAbuseLocked () {
            const configValues = this.contract.fulfillment_contract_config_values || []

            return configValues
                .filter((configValue) => configValue.key === 'abuse_lock')
                .pop()?.value === '1'
        },
        showAbuseLockButton () {
            return this.isVpsContract && this.userPermissions.includes('contract.abuse_lock')
        },
        showAntagonistSuspensionFormButton () {
            return this.userPermissions.includes('contract.edit') && this.contract.fulfillment_module === 'antagonist-da'
        },
        uncoveredFreeMonth () {
            if (this.contract.free_months < 1) return ''

            return this.contract.free_months + ' ' + this.__('customers.contracts.uncovered_free_month')
        },
        possibleInclusiveDomains () {
            const array = []
            for (const [index, value] of Object.entries(this.contract.possible_inclusive_domains)) {
                array.push(value + 'x ' + index + '-Domain')
            }
            return array
        },
        usedInclusiveDomains () {
            const array = []
            for (const [index, value] of Object.entries(this.contract.used_inclusive_domains)) {
                array.push(value + 'x ' + index + '-Domain')
            }
            return array
        },
        hasThirdPartyCost () {
            let hasThirdPartyCost = false

            this.contract.contract_phases.forEach((phase) => {
                if (phase.third_party_cost > 0) hasThirdPartyCost = true
            })

            return hasThirdPartyCost
        },
        contractConfigOptions () {
            const options = {}
            for (const option of Object.values(this.contract.options)) {
                if (option.is_product_setting || option.is_instance_setting) {
                    continue
                }
                options[option.name] = option
            }

            return options
        },
        instanceConfigOptions () {
            const options = {}
            for (const option of Object.values(this.contract.options)) {
                if (option.is_instance_setting === false) {
                    continue
                }
                options[option.name] = option
            }

            return options
        },
        productConfigOptions () {
            const options = {}

            for (const option of Object.values(this.contract.options)) {
                if (option.is_product_setting === false || option.is_instance_setting) {
                    continue
                }
                options[option.name] = option
            }

            return options
        },
        tenantSettings () {
            return this.$page.props.tenantSettings
        },
        calculatedInstallments () {
            if (this.contract.installments === null || this.contract.active_contract_phase === null) {
                return null
            }

            let price = 0

            if (this.contract.one_time === true) {
                price = this.contract.active_contract_phase.discounted_price
            } else {
                price = this.internationalToNormalFormat(this.contract.active_contract_phase.discounted_price)
            }

            const amount = this.internationalToNormalFormat(price) / this.contract.installments

            return __('customers.contracts.installments_amount', { amount: amount.toFixed(2) })
        },
        areDomainOwnerActionsEnabled () {
            return this.customer.brand?.change_domain_owner_data_allowed === true &&
                this.contract.supports_owner_data === true &&
                this.userPermissions.includes('contract.edit') &&
                !['done', 'terminated', 'cancelled'].includes(this.contract.status)
        },
        showMoveZoneButton () {
            return this.userPermissions.includes('contract.fulfillment') && this.contract.domain?.dns_provider.type === 'onehop_dns' && this.contract.parent_contract_id === null
        },
        fulfillmentStrategy () {
            const fulfillmentStrategyId = this.contract.fulfillment_strategy_id ?? this.contract.active_contract_phase.product.fulfillment_strategy_id

            return fulfillmentStrategyId !== null ? __(`products.fulfillmentStrategies.values.${fulfillmentStrategyId}`) : __('customers.contracts.no_fulfillment_strategy')
        },
        extraordinaryTerminationText () {
            if (this.contract.active_termination_extraordinary === false) {
                return ''
            }

            return this.__('customers.contracts.extraordinary')
        },
        isDomainTransfer () {
            if (this.contract.type !== 'domain') {
                return false
            }

            return this.contract.fulfillment_contract_config_values.some(
                option =>
                    (option.key === 'authcode' && option.value?.trim()) ||
                    (option.key === 'force_transfer' && (option.value === '1' || option.value === 1 || option.value === true))
            )
        },
        isVerificationEmailWaitForInput () {
            return this.contract.type === 'domain' &&
                this.contract.status === 'wait_for_input' &&
                this.tenantSettings.domain_email_verification_on_domain_fulfillment === true &&
                this.isDomainTransfer === false
        }
    },
    methods: {
        hasActivityLogChanges,
        copyToClipboard (value) {
            navigator.clipboard.writeText(value)
        },
        getSsoLabel (type) {
            let moduleName = (this._.capitalize(this.fulfillmentAccount.group.module))

            if (this.fulfillmentAccount.group.module === 'kamp_dhp') {
                moduleName = 'Kamp DHP'
            }

            if (this.fulfillmentAccount.group.module === 'kamp_colocation') {
                moduleName = 'Kamp Colocation'
            }

            if (type === 'default' && this.fulfillmentAccount.group.module === 'webcard') {
                moduleName = ''
            }

            return this.__('customers.contracts.login', {
                module_name: moduleName
            })
        },
        navigateToSSO (type) {
            window.axios.get(this.$route('customers.customers.contracts.sso', {
                customer: this.customer,
                contract: this.contract,
                type: type
            }))
                .then((response) => {
                    try {
                        window.open(response.data.url, '_blank').focus()
                    } catch (error) {
                        this.showSSOErrorModal = true
                        this.ssoErrorMessage = this.__('customers.contracts.sso_popup_error_message')
                    }
                })
                .catch((error) => {
                    this.showSSOErrorModal = true
                    this.ssoErrorMessage = error.response?.data?.errorMessage ?? this.__('customers.contracts.sso_generation_error_message')
                })
        },
        viewResellerContracts () {
            return this.$inertia.get(this.$route('customers.customers.contracts.reseller_contracts', {
                customer: this.contract.customer_id,
                contract: this.contract.id
            }))
        },
        viewProviderboxContracts () {
            return this.$inertia.get(this.$route('customers.customers.contracts.providerbox_contracts', {
                customer: this.contract.customer_id,
                contract: this.contract.id
            }))
        },
        viewPartnerContracts () {
            return this.$inertia.get(this.$route('customers.customers.contracts.partner_contracts', {
                customer: this.contract.customer_id,
                contract: this.contract.id
            }))
        },
        upgradeContract () {
            this.showUpgradeModal = true
        },
        downgradeContract () {
            this.showDowngradeModal = true
        },
        viewChangePleskModule () {
            this.showChangePleskModuleModal = true
        },
        upgradeFreeMonths () {
            this.showFreeMonthsModal = true
        },
        closePriceOverview () {
            this.showPriceOverviewModal = false
            this.$inertia.reload()
        },
        closeRuntimeOverview () {
            this.showRuntimeOverviewModal = false
            this.$inertia.reload()
        },
        closeDowngrade (changed = false) {
            this.showDowngradeModal = false
            if (changed) {
                this.$inertia.reload()
            }
        },
        closeUpgrade (changed = false) {
            this.showUpgradeModal = false
            if (changed) {
                this.$inertia.reload()
            }
        },
        closeProductChange () {
            this.showProductChangeModal = false
            this.$inertia.reload()
        },
        closeFreeMonths () {
            this.showFreeMonthsModal = false
            this.$inertia.reload()
        },
        showPriceOverview () {
            this.priceOverviewLoading = true

            window.axios.post(this.$route('customers.customers.contracts.price_overview', {
                customer: this.customer,
                contract: this.contract
            }))
                .then((response) => {
                    this.groupedContractPhases = response.data.groupedContractPhases
                    this.showPriceOverviewModal = true
                    this.priceOverviewLoading = false
                })
        },
        openOneTimeDiscountModal () {
            window.axios.get(this.$route('customers.customers.contracts.one_time_discount.form', {
                customer: this.customer,
                contract: this.contract
            }))
                .then((response) => {
                    this.oneTimeDiscountAvailable = response.data.discountAvailable
                    this.oneTimeDiscountMaxPercentage = response.data.maxPercentage
                    this.showOneTimeDiscountModal = true
                })
        },
        showRuntimeOverview () {
            this.runtimeOverviewLoading = true

            window.axios.post(this.$route('customers.customers.contracts.runtime_overview', {
                customer: this.customer,
                contract: this.contract
            }))
                .then((response) => {
                    this.groupedContractPhases = response.data.groupedContractPhases
                    this.showRuntimeOverviewModal = true
                    this.runtimeOverviewLoading = false
                })
        },
        downloadFile () {
            if (!this.contract.terms_and_conditions_id) return

            window.open(
                this.$route('settings.terms-and-conditions.download', {
                    termsAndConditions: this.contract.terms_and_conditions_id
                }),
                '_blank'
            )
        },
        downloadContractFile (fileId) {
            window.open(
                this.$route('customers.customers.customer-files.download', {
                    customer: this.customer.id,
                    customerFile: fileId
                }),
                '_blank'
            )
        },
        isEditableOrDeleteable (contract, contractPhase) {
            // If you edit the logic here, please edit it also in (*intentionally* duplicated code):
            // resources/js/Pages/Customers/ContractPhases/Create.vue
            if (this.contract.one_time !== true || this.contract.status === 'terminated' || this.contract.status === 'cancelled' || contractPhase.id === contract.active_contract_phase.id) {
                return false
            }

            return this.userPermissions.includes('contract.edit')
        },
        deleteContractPhase (contractPhase) {
            this.deleteContractPhaseCallBack = () => {
                this.$inertia.delete(this.$route('customers.customers.contracts.phases.destroy', {
                    customer: this.contract.customer_id, contract: this.contract.id, phase: contractPhase.id
                }),
                {
                    preserveScroll: true
                }
                )
                this.showContractPhaseDeleteConfirmationModal = false
            }
            this.showContractPhaseDeleteConfirmationModal = true
        },
        discountType (type, value) {
            switch (type) {
                case 'percentage':
                    return this.pricePercentage(value)
                case 'fixed':
                    return this.priceFormat(value, 2, this.customer.market_region_id)
            }
        },
        terminateContract () {
            window.axios.get(this.$route('contracts.termination_dates', { contract: this.contract.id }))
                .then(response => {
                    this.terminationDates = response.data
                })
            this.showTerminationModal = true
        },
        cancelContractTermination () {
            this.showConfirmationModal = true
        },
        terminateContractSilently () {
            this.showTerminateSilentlyModal = true
        },
        applyMoneyBackGuarantee () {
            this.showGuaranteeConfirmationModal = true
        },
        withdrawContract () {
            this.showWithdrawConfirmationModal = true
        },
        quickstartContract () {
            this.showQuickstartConfirmationModal = true
        },
        moveContract () {
            if (this.contract.is_movable) {
                this.showMoveContractModal = true
                this.showDisabledMoveContractModal = false
            } else {
                this.showMoveContractModal = false
                this.showDisabledMoveContractModal = true
            }
        },
        updateTerminationDates (newDates) {
            this.terminationDates = newDates
        },
        clickUnsuspend () {
            this.showUnsuspendConfirmationModal = true
        },
        clickAbuseLock () {
            this.abuseLockEnable = !this.isAbuseLocked
            this.showAbuseLockModal = true
        },
        clickRelease () {
            this.showReleaseConfirmationModal = true
        },
        openAuthCodeConfirmationModal () {
            this.showSendAuthCodeModal = true
        },
        restartTransfer () {
            this.showRestartTransferModal = true
        },
        clickDelete () {
            this.deleteModalMessage = this.__('customers.contracts.delete_confirm_message')
                .replace(':contractId', this.contract.id)
                .replace(':productName', this.contract.product_name)

            this.showDeleteConfirmationModal = true
        },
        productChange () {
            this.showProductChangeModal = true
        },
        restoreContract () {
            this.showRestoreContractModal = true
        },
        // Backed by ContractInvoicingExclusionService, so this page and every API
        // consumer answer "is this invoiced?" from the same rule. It replaces a local
        // re-derivation that covered only three of the triggers.
        isExcludedFromInvoicing () {
            return Boolean(this.contract.invoicing_exclusion)
        },
        synchronizeOwnerData () {
            this.$inertia.post(this.$route('customers.customers.contracts.synchronize-domain-contact-details', {
                customer: this.contract.customer_id,
                contract: this.contract.id
            }))
        },
        triggerEmailVerification () {
            this.$inertia.post(this.$route('customers.customers.contracts.trigger-email-verification', {
                customer: this.contract.customer_id,
                contract: this.contract.id
            }))
        },
        resendEmailVerificationEmail () {
            this.$inertia.post(this.$route('customers.customers.contracts.resend-verification-email', {
                customer: this.contract.customer_id,
                contract: this.contract.id
            }))
        },
        triggerOwnerChange () {
            this.$inertia.post(this.$route('customers.customers.contracts.trigger-owner-change', {
                customer: this.contract.customer_id,
                contract: this.contract.id
            }))
        },
        showEditOwnerDataModal () {
            this.showEditDomainContactDataModal = true
        },
        hideEditOwnerDataModal () {
            this.showEditDomainContactDataModal = false
        },
        viewChangesModal (item, subject) {
            this.changes = activityLogChanges(item)
            this.subject = subject
            this.showChangesModal = true
        },
        subjectText (item) {
            if (item.subject_model) {
                return `${item.subject_model.name} (${item.subject_id})`
            }

            return item.subject_id
        },
        comparePhaseProperty (index, property) {
            if (index === 0) {
                return ''
            }

            const currentPhase = this.contract.contract_phases[index]
            const previousPhase = this.contract.contract_phases[index - 1]

            const currentValue = this.getPhasePropertyValue(currentPhase, property)
            const previousValue = this.getPhasePropertyValue(previousPhase, property)

            if (currentValue !== previousValue) {
                return 'text-warning'
            }

            return ''
        },
        getPhasePropertyValue (object, property) {
            return property.split('.').reduce((o, p) => (o ? o[p] : undefined), object)
        },
        renewDNSSec (shouldRegenerateKeys) {
            this.$inertia.post(this.$route('customers.customers.contracts.renew-dnssec', {
                customer: this.contract.customer_id,
                contract: this.contract.id,
                regenerateKeys: shouldRegenerateKeys ? 1 : 0
            }))
        },
        disableDNSSec () {
            this.$inertia.post(this.$route('customers.customers.contracts.disable-dnssec', {
                customer: this.contract.customer_id,
                contract: this.contract.id
            }))
        },
        resetContract () {
            this.$inertia.post(this.$route('customers.customers.contracts.reset-contract', {
                customer: this.contract.customer_id,
                contract: this.contract.id
            }))
        },
        reissueSSLCertificate () {
            this.$inertia.post(this.$route('customers.customers.contracts.reissue-ssl-certificate', {
                customer: this.contract.customer_id,
                contract: this.contract.id
            }))
        },
        displayMoveZoneModal () {
            this.showMoveZoneModal = true
        },
        getPreviousContractUrl (contractId) {
            return this.$route('customers.customers.contracts.show', {
                customer: this.customer.id,
                contract: contractId
            })
        },
    }
}
</script>

<style lang="scss">
.customer-contract {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 15px;

    & > div:not(.d-fabs) {
        display: flex;
        flex-direction: column;
        grid-gap: 15px;
    }
    & .detail {
        display: flex;
        flex-direction: column;
        grid-gap: 15px;

        & > div {
            display: grid;
            grid-template-columns: 2fr 2fr 2fr;
            grid-gap: 15px;
            align-items: center;

            &.two-columns {
                grid-template-columns: 1fr 2fr 0fr;
            }

            & span:nth-child(1) {
                font-weight: bold;
            }

            & span:nth-child(2) {
                line-height: 1.5em;
                word-break: break-all;
            } & button {
                justify-self: end;
                & span {
                    font-weight: normal !important;
                }
            }
        }
    }
    & .config-option-details {
        display: flex;
        flex-direction: column;
        grid-gap: 15px;

        & > div > div {
            display: grid;
            grid-template-columns: 2fr 2fr 2fr;
            grid-gap: 15px;
            align-items: center;

            &.two-columns {
                grid-template-columns: 1fr 2fr 0fr;
            }

            & span:nth-child(1) {
                font-weight: bold;
            }

            & span:nth-child(2) {
                line-height: 1.5em;
                word-break: break-all;
            } & button {
                  justify-self: end;
                  & span {
                      font-weight: normal !important;
                  }
              }
        }
    }
    & .contract-phases {
        & table {
            width: 100%;

            & thead {
                border-bottom: 1px solid var(--colorAppBorder);

                & th {
                    padding: 10px 15px;
                }
            }
            & tbody {
                & td {
                    padding: 10px 15px;
                }
            }
        }
    }
    .terminate-dropdown {
        background: var(--colorSecondary) !important;
        color: var(--colorSecondaryText) !important;
        outline-color: rgb(16, 16, 16);
    }
    .terminate-dropdown:hover button {
        background: var(--colorSecondaryHover) !important;
    }
    .info-message {
        margin-bottom: 0;
    }
}

.cutting-line {
    border-bottom: 1px solid var(--colorAppBorder);
    display: block;
}

.no-files {
    padding: 30px 15px;
    text-align: center;
    opacity: 0.6;
}

.file {
    padding: 15px;
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-gap: 15px;
    align-items: center;

    & .mdi {
        opacity: 0.4;
    }

    & > .meta {
        display: grid;
        grid-gap: 10px;

        & .filename {
            font-weight: bold;
        }
        & .description {
            word-break: break-all;
        }
        & .meta {
            opacity: 0.6;
            font-size: 12px;
        }
    }

    &:not(:last-child) {
        border-bottom: 1px solid #D9DBE0;
    }
}

.contract-status-grid {
    grid-template-columns: 2fr 2.5fr 1.5fr !important;
}
.text-space-wrap {
    white-space: pre-wrap;
}
.detail-log {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 10px;
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 50%;
}
.second-detail-log {
    & span {
        text-align: end;
    }
}
.detail-log-td {
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid lightgray;
}
.detail-log-header {
    display: flex;
    justify-content: space-between;
    padding: 10px 15px;
}
</style>
